<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import type { UploadedFile } from '~/types/models';

const props = withDefaults(defineProps<{
    open: boolean;
    file?: UploadedFile | null;
    saving?: boolean;
    error?: string;
}>(), {
    file: null,
    saving: false,
    error: '',
});

const emit = defineEmits<{
    (event: 'update:open', value: boolean): void;
    (event: 'save', payload: {
        file: UploadedFile;
        caption: string;
        altText: string;
        visibleInPortal: boolean;
    }): void;
}>();

const caption = ref('');
const altText = ref('');
const visibleInPortal = ref(true);

watch(() => [props.open, props.file?.id] as const, ([open]) => {
    if (!open || !props.file) return;
    caption.value = props.file.galleryCaption || '';
    altText.value = props.file.galleryAltText || '';
    visibleInPortal.value = props.file.galleryVisibleInPortal !== false;
}, { immediate: true });

function save() {
    if (!props.file) return;
    emit('save', {
        file: props.file,
        caption: caption.value.trim(),
        altText: altText.value.trim(),
        visibleInPortal: visibleInPortal.value,
    });
}
</script>

<template>
    <Sheet :open="open" @update:open="emit('update:open', $event)">
        <SheetContent class="w-full overflow-y-auto sm:max-w-lg">
            <SheetHeader class="text-left">
                <SheetTitle>Edit image details</SheetTitle>
                <SheetDescription>Update the caption, accessible description, and sharing details.</SheetDescription>
            </SheetHeader>

            <div v-if="file" class="space-y-5 px-4 pb-4">
                <div class="flex h-56 items-center justify-center overflow-hidden rounded-xl border bg-muted/40 p-4">
                    <img
                        :src="file.fileURL"
                        :alt="altText || caption || file.fileName"
                        class="max-h-full w-full object-contain"
                    >
                </div>

                <div class="space-y-2">
                    <Label for="uploaded-image-caption">Caption</Label>
                    <Textarea id="uploaded-image-caption" v-model="caption" rows="4" placeholder="Add context or a short note for this image…" />
                </div>

                <div class="space-y-2">
                    <Label for="uploaded-image-alt-text">Alt text</Label>
                    <Input id="uploaded-image-alt-text" v-model="altText" placeholder="Accessible image description" />
                </div>

                <label v-if="file.entityType === 'project'" class="flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-sm">
                    <span>Visible in client portal</span>
                    <Switch v-model:checked="visibleInPortal" />
                </label>

                <p v-if="error" class="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive" role="alert">
                    {{ error }}
                </p>
            </div>

            <SheetFooter>
                <Button type="button" variant="ghost" :disabled="saving" @click="emit('update:open', false)">Cancel</Button>
                <Button type="button" :disabled="!file || saving" @click="save">
                    {{ saving ? 'Saving…' : 'Save changes' }}
                </Button>
            </SheetFooter>
        </SheetContent>
    </Sheet>
</template>
